import React, { useState, useEffect, useRef, useMemo } from "react";
import { api } from "./api";
import "./App.css";

const COURTS = [
  { id: "1", name: "Court 1", desc: "Standard Indoor Court", img: "/court1.jpg" },
  { id: "2", name: "Court 2", desc: "Standard Indoor Court", img: "/court2.jpg" },
  { id: "3", name: "Court 3", desc: "Standard Indoor Court", img: "/court3.jpg" },
];

const TIME_SLOTS = [
  "08:00-09:00","09:00-10:00","10:00-11:00","11:00-12:00",
  "12:00-13:00","13:00-14:00","14:00-15:00","15:00-16:00",
  "16:00-17:00","17:00-18:00","18:00-19:00","19:00-20:00",
  "20:00-21:00","21:00-22:00",
];

const fmtTime = (s) => {
  const [h] = s.split(":");
  const n = parseInt(h, 10);
  return `${String(n > 12 ? n - 12 : n || 12).padStart(2, "0")}:00 ${n >= 12 ? "PM" : "AM"}`;
};

const DAYS = ["SUN","MON","TUE","WED","THU","FRI","SAT"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const phNow = () => {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Manila", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t).value;
  return new Date(+get("year"), +get("month") - 1, +get("day"), +get("hour"), +get("minute"), +get("second"));
};
const phToday = () => {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Manila", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t).value;
  return `${get("year")}-${get("month")}-${get("day")}`;
};

function Calendar({ value, onChange, maxDate }) {
  const today = phNow(); today.setHours(0,0,0,0);
  const [view, setView] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const days = useMemo(() => {
    const y = view.getFullYear(), m = view.getMonth();
    const first = new Date(y, m, 1).getDay();
    const last = new Date(y, m + 1, 0).getDate();
    const prevLast = new Date(y, m, 0).getDate();
    const cells = [];
    for (let i = first - 1; i >= 0; i--) cells.push({ d: prevLast - i, cur: false });
    for (let i = 1; i <= last; i++) cells.push({ d: i, cur: true });
    const rem = 7 - (cells.length % 7); if (rem < 7) for (let i = 1; i <= rem; i++) cells.push({ d: i, cur: false });
    return cells;
  }, [view]);

  const toStr = (d) => {
    const y = view.getFullYear(), m = view.getMonth();
    return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  };

  const isPast = (d) => {
    const dt = new Date(view.getFullYear(), view.getMonth(), d);
    return dt < today || (maxDate && dt > maxDate);
  };

  return (
    <div className="calendar">
      <div className="cal-header">
        <button onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}>&lt;</button>
        <span>{MONTHS[view.getMonth()]} {view.getFullYear()}</span>
        <button onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}>&gt;</button>
      </div>
      <div className="cal-days">{DAYS.map((d) => <span key={d}>{d}</span>)}</div>
      <div className="cal-grid">
        {days.map((c, i) => {
          const dateStr = c.cur ? toStr(c.d) : null;
          const disabled = !c.cur || isPast(c.d);
          const selected = dateStr === value;
          return (
            <button key={i} className={`cal-cell${!c.cur ? " other" : ""}${selected ? " selected" : ""}${disabled ? " disabled" : ""}`}
              disabled={disabled} onClick={() => dateStr && onChange(dateStr)}>
              {c.d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const CAROUSEL_IMAGES = [
  "/carousel/221981c4-e11b-429f-a021-14bfcbb58e73.jpeg",
  "/carousel/3eebe184-591a-4509-a09a-65773f701919.jpeg",
  "/carousel/507de016-8e69-4d83-9c1a-9d919dcd000f.jpeg",
  "/carousel/58634122-366a-4593-bb2c-b11eb4c89fcd.jpeg",
  "/carousel/5e59c9aa-1b8f-432a-85dc-bb49d6e98cb5.jpeg",
];

function Carousel() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setIdx((i) => (i + 1) % CAROUSEL_IMAGES.length), 4000);
    return () => clearInterval(iv);
  }, []);
  return (
    <div className="carousel">
      <div className="carousel-track" style={{ transform: `translateX(-${idx * 100}%)` }}>
        {CAROUSEL_IMAGES.map((src, i) => <img key={i} src={src} alt="" className="carousel-img" />)}
      </div>
      <div className="carousel-dots">
        {CAROUSEL_IMAGES.map((_, i) => <button key={i} className={`carousel-dot${i === idx ? " active" : ""}`} onClick={() => setIdx(i)} />)}
      </div>
    </div>
  );
}

function App() {
  const [date, setDate] = useState(phToday);
  const [selected, setSelected] = useState([]); // [{ court, slot }]
  const [bookings, setBookings] = useState([]); // all bookings for the date
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState(null);
  const [agree1, setAgree1] = useState(false);
  const [agree2, setAgree2] = useState(false);
  const [courtStatuses, setCourtStatuses] = useState({});
  const [allPricing, setAllPricing] = useState(null);
  const [maxAdvanceDays, setMaxAdvanceDays] = useState(30);
  const [closures, setClosures] = useState([]);
  const fileRef = useRef();

  useEffect(() => {
    api.getCourtStatuses().then(setCourtStatuses).catch(() => {});
    Promise.all(COURTS.map((c) => api.getPricing(c.id).then((d) => [c.id, d.rules || []]).catch(() => [c.id, []])))
      .then((entries) => setAllPricing(Object.fromEntries(entries)));
    api.getSettings().then((s) => setMaxAdvanceDays(s.maxAdvanceDays || 30)).catch(() => {});
    api.getClosures().then((c) => setClosures(c.filter((x) => !x.deleted))).catch(() => {});
  }, []);

  const pricingLoaded = allPricing !== null;

  const courtPriceLabel = (c) => {
    if (!pricingLoaded) return null;
    const rules = allPricing[c.id];
    if (!rules?.length) return "No price set";
    const rates = rules.map((r) => r.rate);
    const min = Math.min(...rates), max = Math.max(...rates);
    return min === max ? `PHP ${min}/hr` : `PHP ${min}–${max}/hr`;
  };

  const availableCourts = COURTS.filter((c) => {
    const s = courtStatuses[c.id];
    return s ? s.available !== false : true;
  });

  const getSlotRate = (courtId, slot) => {
    const rules = allPricing?.[courtId] || [];
    const start = slot.split("-")[0];
    const rule = rules.find((r) => start >= r.start && start < r.end);
    return rule ? rule.rate : 0;
  };

  const subtotal = selected.reduce((sum, s) => sum + getSlotRate(s.court, s.slot), 0);
  const convenienceFee = selected.length * 15;
  const total = subtotal + convenienceFee;

  useEffect(() => {
    if (date) {
      api.getBookings({ date }).then(setBookings).catch(() => setBookings([]));
    }
    setSelected([]);
  }, [date]);

  const showToast = (text, type = "success") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  const isBooked = (courtId, time) =>
    bookings.some((b) => b.court === courtId && b.date === date && b.time === time && b.status !== "declined");

  const isClosed = (courtId, time) =>
    closures.some((c) => c.court === courtId && c.date === date && c.slots?.includes(time));

  const isElapsed = (slot) => {
    if (date !== phToday()) return false;
    const now = phNow();
    const [startH] = slot.split("-")[0].split(":");
    return now.getHours() >= parseInt(startH, 10);
  };

  const isCourtBlockedForDate = (courtId) => {
    const s = courtStatuses[courtId];
    if (!s?.blockedDays) return false;
    if (s.blockedDays.startDate && date < s.blockedDays.startDate) return false;
    const dayOfWeek = new Date(date + "T00:00").getDay();
    return s.blockedDays.days?.includes(dayOfWeek);
  };

  const isSlotBlockedByDayForCourt = (courtId, slot) => {
    const s = courtStatuses[courtId];
    if (!s?.blockedDays?.partialDays) return false;
    if (s.blockedDays.startDate && date < s.blockedDays.startDate) return false;
    const dayOfWeek = String(new Date(date + "T00:00").getDay());
    const partial = s.blockedDays.partialDays[dayOfWeek];
    if (!partial) return false;
    return !partial.allowed.includes(slot);
  };

  const isSlotUnavailable = (courtId, slot) =>
    isBooked(courtId, slot) || isClosed(courtId, slot) || isCourtBlockedForDate(courtId) || isSlotBlockedByDayForCourt(courtId, slot);

  const toggleSlot = (courtId, time) => {
    if (!date) { showToast("Please select a date first.", "warn"); return; }
    setSelected((prev) => {
      const exists = prev.find((s) => s.court === courtId && s.slot === time);
      if (exists) return prev.filter((s) => !(s.court === courtId && s.slot === time));
      return [...prev, { court: courtId, slot: time }];
    });
  };

  const handleReceipt = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setReceipt(file);
    const r = new FileReader();
    r.onload = (ev) => setReceiptPreview(ev.target.result);
    r.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!date || !selected.length || !name.trim() || !email.trim() || !phone.trim() || !receipt) return;
    setSubmitting(true);
    try {
      const { uploadUrl, key } = await api.getUploadUrl(receipt.name, receipt.type);
      await api.uploadReceipt(uploadUrl, receipt);
      // Group selected by court
      const byCourt = {};
      selected.forEach((s) => { if (!byCourt[s.court]) byCourt[s.court] = []; byCourt[s.court].push(s.slot); });
      // Submit one booking per court
      for (const [courtId, slots] of Object.entries(byCourt)) {
        slots.sort((a, b) => TIME_SLOTS.indexOf(a) - TIME_SLOTS.indexOf(b));
        await api.createBooking({
          name: name.trim(), phone: phone.trim(), email: email.trim(),
          court: courtId, date, slots, receiptKey: key,
          consentData: true, consentTimestamp: new Date().toISOString(),
        });
      }
      setSubmitted(true);
      showToast("Booking submitted successfully!");
    } catch (err) {
      showToast(err.message, "warn");
    }
    setSubmitting(false);
  };

  const reset = () => {
    setDate(phToday()); setSelected([]); setName(""); setEmail("");
    setPhone(""); setReceipt(null); setReceiptPreview(null); setSubmitted(false); setAgree1(false); setAgree2(false);
  };

  const fmtDate = (d) => {
    const dt = new Date(d + "T00:00");
    return dt.toLocaleDateString("en-PH", { month: "long", day: "numeric", year: "numeric" });
  };

  return (
    <div className="app">
      <header className="header">
        <img src="/logo.jpeg" alt="ALP Pickleball Hub" className="logo" />
        <h1>Pickleball Court Reservation</h1>
        <p>Community and Fitness at the Heart of the Parish</p>
      </header>

      <Carousel />

      <div className="main-layout">
        <div className="left-col">
          {/* Step 1: Select Court */}
          <section>
            <h2><span className="step-badge">1</span> Courts & Pricing</h2>
            <div className="court-cards">
              {availableCourts.map((c) => (
                <div key={c.id} className="court-card">
                  <div className="court-img" style={{ backgroundImage: `url(${c.img})` }}>
                    <span className="court-price">{courtPriceLabel(c) || <span className="price-loading">⏳</span>}</span>
                  </div>
                  <div className="court-info">
                    <strong>{c.name}</strong>
                    <span>{c.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Step 2 & 3: Date + Time side by side */}
          <div className="date-time-row">
            <section>
              <h2><span className="step-badge">2</span> Select Date</h2>
              <Calendar value={date} onChange={setDate} maxDate={(() => { const d = phNow(); d.setDate(d.getDate() + maxAdvanceDays); return d; })()} />
              <p className="donation-note">Thank you for your monetary donation. With this DONATION, you are helping the ALP in the upkeep of its premises and the maintenance of its facilities. You are also particularly supporting the ALP's pastoral outreach/mission especially the catechism to students in schools within its jurisdiction.</p>
            </section>
            <section>
              <h2><span className="step-badge">3</span> Select Time & Court</h2>
              {availableCourts.map((c) => (
                <div key={c.id} className="court-time-section">
                  <h3 className="court-time-label">{c.name}</h3>
                  {TIME_SLOTS.every((slot) => isElapsed(slot) || isSlotUnavailable(c.id, slot)) ? (
                    <div className="empty-slots"><p>No available slots</p></div>
                  ) : (
                  <div className="time-grid">
                    {TIME_SLOTS.map((slot) => {
                      const elapsed = isElapsed(slot);
                      if (elapsed) return null;
                      const unavailable = isSlotUnavailable(c.id, slot);
                      const sel = selected.some((s) => s.court === c.id && s.slot === slot);
                      return (
                        <button key={slot} className={`time-slot${sel ? " selected" : ""}${unavailable ? " booked" : ""}`}
                          disabled={unavailable} onClick={() => toggleSlot(c.id, slot)}>
                          {fmtTime(slot.split("-")[0])}
                          <span className={`slot-badge${sel ? " sel" : unavailable ? " bkd" : ""}`}>
                            {isBooked(c.id, slot) ? "BOOKED" : (isClosed(c.id, slot) || isCourtBlockedForDate(c.id) || isSlotBlockedByDayForCourt(c.id, slot)) ? "CLOSED" : sel ? "✓" : "AVAIL"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  )}
                </div>
              ))}
            </section>
          </div>
        </div>

        {/* Right: Booking Summary */}
        <aside className="summary-panel">
          <h3>Booking Summary</h3>
          {submitted ? (
            <div className="success-msg">
              <div className="success-icon">✅</div>
              <p>Your booking has been submitted and is pending admin approval.</p>
              <button className="btn-primary" onClick={reset}>Book Another</button>
            </div>
          ) : (
            <>
              <div className="summary-details">
                <div className="summary-row"><span>DATE</span><strong>{date ? fmtDate(date) : "—"}</strong></div>
                {selected.length > 0 ? (() => {
                  const byCourt = {};
                  selected.forEach((s) => { if (!byCourt[s.court]) byCourt[s.court] = []; byCourt[s.court].push(s.slot); });
                  return Object.entries(byCourt).map(([cId, slots]) => {
                    slots.sort((a, b) => TIME_SLOTS.indexOf(a) - TIME_SLOTS.indexOf(b));
                    const range = `${fmtTime(slots[0].split("-")[0])} - ${fmtTime(slots[slots.length - 1].split("-")[1])}`;
                    return <div key={cId} className="summary-row"><span>COURT {cId}</span><strong>{range} ({slots.length}hr)</strong></div>;
                  });
                })() : <div className="summary-row"><span>COURTS</span><strong>—</strong></div>}
                <div className="summary-row"><span>SUBTOTAL</span><strong>PHP {subtotal.toFixed(2)}</strong></div>
                <div className="summary-row"><span>KIND DONATION</span><strong>PHP {convenienceFee.toFixed(2)}</strong></div>
                <div className="summary-row total"><span>Total</span><strong>PHP {total.toFixed(2)}</strong></div>
              </div>

              <div className="summary-form">
                <label>FULL NAME</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Juan dela Cruz" />
                <label>EMAIL ADDRESS</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="juan@email.com" />
                <label>CONTACT NUMBER</label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+63 9XX XXX XXXX" />

                <label>SCAN TO DONATE</label>
                <div className="qr-codes">
                  <img src="/qr1.jpg" alt="QR Code 1" className="qr-img" onClick={(e) => { e.currentTarget.classList.toggle("qr-enlarged"); }} />
                  <img src="/qr4.jpeg" alt="QR Code 2" className="qr-img" onClick={(e) => { e.currentTarget.classList.toggle("qr-enlarged"); }} />
                </div>
                <p className="qr-hint">Click to enlarge</p>
                <div className="pay-numbers">
                  <p>Or send donation to:</p>
                  <p><strong>09605269280</strong> or <strong>09999401736</strong></p>
                </div>

                <label>PROOF OF DONATION (GCASH/BANK)</label>
                <div className="upload-area" onClick={() => fileRef.current?.click()}>
                  {receiptPreview ? (
                    <div className="preview-wrap">
                      <img src={receiptPreview} alt="Receipt" className="receipt-preview" />
                      <p className="preview-name">{receipt.name}</p>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <span className="upload-icon">☁</span>
                      <p>Upload a file</p>
                      <p className="upload-hint">PNG, JPG up to 10MB</p>
                    </div>
                  )}
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleReceipt} hidden />
                </div>

                <div className="consent-section">
                  <label className="consent-check">
                    <input type="checkbox" checked={agree1} onChange={(e) => setAgree1(e.target.checked)} />
                    <span>I hereby declare that I have freely submitted my personal information to the Ascension of the Lord Parish (ALP) for the sole purpose of booking the subject time slot at the pickleball courts located at its Pastoral Center.</span>
                  </label>
                  <label className="consent-check">
                    <input type="checkbox" checked={agree2} onChange={(e) => setAgree2(e.target.checked)} />
                    <span>I understand that all individuals availing themselves of these courts assume the risks of accident, hazards, and bodily injuries that may happen thereat; and thus release and discharge the ALP and/or any of its volunteers and workers from liability for such injuries and for property damage, loss, or theft that may arise therefrom.</span>
                  </label>
                </div>

                <button className="btn-confirm" disabled={!date || !selected.length || !name.trim() || !email.trim() || !phone.trim() || !receipt || !agree1 || !agree2 || submitting}
                  onClick={handleSubmit}>
                  {submitting ? "Submitting..." : "Confirm Booking"}
                </button>
                <p className="disclaimer">By clicking "Confirm Booking", you agree to follow ALP Court Rules and Liturgical Guidelines.</p>
              </div>
            </>
          )}
        </aside>
      </div>

      <footer className="footer">
        <span>Ascension of the Lord Parish</span>
        <span>© {new Date().getFullYear()} Ascension of the Lord Parish. All Rights Reserved.</span>
      </footer>

      {toast && <div className={`toast toast-${toast.type}`}>{toast.text}</div>}
    </div>
  );
}

export default App;
